import {
  PeriodUnit,
  ProductFormData,
  ProductOffer,
  ProductTypes,
  ReBillTimesType,
  schemaProduct,
  StartDateCalculation,
} from '@src/types/productsAddons.ts';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ProductForm } from '@features/admin/productsAddons';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createProduct,
  getProductDetail,
  saveProduct,
  saveStatus,
} from '@src/services/products.ts';
import { buildProduct, mapperProductData } from '@lib/products.ts';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { isEmpty } from 'lodash';
import queryString from 'query-string';
import { Button } from '@features/ui';
import { useProductOptions } from '@hooks/useProductOptions.tsx';
import { TOption, TradingPlatForms } from '@src/types';
import { getTradovateTemplateAccounts } from '@src/services/dropdown.ts';
import { useTradovateTemplateStore } from '@stores/tradovateTemplateStore.ts';

interface ProductEditProps {
  isEdit?: boolean;
}
export const ProductEdit: FC<ProductEditProps> = ({ isEdit }) => {
  const [productDetail, setProductDetail] = useState<ProductOffer | null>(null);
  const [isLoading, setLoading] = useState(false);

  const navigate = useNavigate();
  const hasRun = useRef(false);
  const params = useParams();
  const location = useLocation();
  const { productOptions } = useProductOptions();
  const { setTradovateOptions } = useTradovateTemplateStore();

  const methods = useForm<ProductFormData>({
    resolver: yupResolver(schemaProduct),
    defaultValues: {
      category: ProductTypes.TRADING_ACCOUNT,
      company: TradingPlatForms.RITHMIC_DATA_FEED,
      start_date_calculation: StartDateCalculation.PAYMENT_DATE,
      name: 'Default Biiling Plan',
      currency: 'usd',
      change_quantity: 1,
      quantity: 1,
      payout_request_trigger: 'last_saturday',
      first_period_unit: PeriodUnit.Day,
      second_period_unit: PeriodUnit.Day,
      rebill_times_type: ReBillTimesType.NoMoreCharges,
    },
  });

  const route = useMemo(() => {
    const parseData = queryString.parse(location.search, {
      parseNumbers: true,
    });

    if (!parseData) {
      return null;
    }

    return parseData as unknown as { templateProductId: number };
  }, [location.search]);

  const fetchProductDetail = async (productId: number) => {
    const { data } = await getProductDetail(productId);

    setProductDetail(data);

    return data;
  };

  useEffect(() => {
    if (!isEdit || !params.id) return;
    setLoading(true);
    fetchProductDetail(Number(params.id))
      .then((res) => {
        const productFormData = mapperProductData(res);

        console.log(productFormData);

        methods.reset(productFormData);
      })
      .catch(() => {
        if (hasRun.current) return;
        hasRun.current = true;
        alert('Product not found!');
        navigate('/admin/products-addons');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, params.id]);

  useEffect(() => {
    if (isEdit || !route?.templateProductId) return;
    fetchProductDetail(route.templateProductId).then((res) => {
      const productFormData = mapperProductData(res);

      console.log(productFormData);

      methods.reset(productFormData);
    });
  }, [isEdit, methods, route?.templateProductId]);

  const fetchDropdownTradovateTemplateAccounts = useCallback(async () => {
    const dropdownTradovateTemplateAccountsRes =
      await getTradovateTemplateAccounts();

    const options: TOption[] = dropdownTradovateTemplateAccountsRes.data?.map(
      (item) => ({
        value: item.value?.toString(),
        label: item.label,
      })
    );

    setTradovateOptions(options);
  }, [setTradovateOptions]);

  useEffect(() => {
    fetchDropdownTradovateTemplateAccounts().then();
  }, [fetchDropdownTradovateTemplateAccounts]);

  const handleSubmitForm = async (data: ProductFormData) => {
    console.log('methods.formState.errors', methods.formState.errors);
    if (!isEmpty(methods.formState.errors)) return;
    try {
      const makeProducts = buildProduct(data, params.id);

      if (!isEdit) {
        const res = await createProduct(makeProducts);

        if (res.data.product?.id) {
          navigate(`/admin/products-addons`);
        }

        return;
      }
      if (!params.id) return;
      await saveProduct(makeProducts, Number(params.id));
      toast.success('Product saved successfully');
    } catch {
      toast.error('Something went wrong. Please try again later.');
    }
  };

  const handleChangeStatus = useCallback(async () => {
    const productId = Number(params.id);

    await saveStatus(productId);
    await fetchProductDetail(productId);
  }, [params.id]);

  console.log('methods.formState.errors', methods.formState.errors);

  return (
    <FormProvider {...methods}>
      <form
        autoComplete="off"
        onSubmit={methods.handleSubmit(handleSubmitForm)}
      >
        <div className={'flex justify-between'}>
          <div className={'flex justify-between mb-4'}>
            <div className={'flex flex-col gap-y-2.5'}>
              <p className={'text-[23px] font-medium text-blue'}>
                {isEdit ? 'Edit Product' : 'Create Product'}
              </p>
              <div className={'h-[2px] w-full bg-blue'} />
            </div>
          </div>
          {isEdit && !isLoading && (
            <Button
              size={'sm'}
              variant={productDetail?.is_active ? 'default' : 'lime'}
              onClick={handleChangeStatus}
            >
              {productDetail?.is_active ? 'Inactive' : 'Active'}
            </Button>
          )}
        </div>
        <ProductForm
          instrumentData={productDetail?.rithmic_instruments ?? []}
          isEdit={isEdit}
          productOptions={productOptions}
          {...methods}
        />
      </form>
    </FormProvider>
  );
};
